// The KYC form of MdpOnboarding / IntercashOnboarding / GscOnboarding: its defaults, its checks and what is sent.
import { type OnboardingForm, type OnboardingProvider } from './api';

export const ONBOARDING_MESSAGES = {
  noAccount: "We couldn't find your account. Please sign out and sign in again.",
  datesRequired: 'Date of birth and ID expiry date are required.',
  // W13: InterCash's own limits on a name (the API says the same: INTERCASH_NAME_MESSAGES, D29)
  interCashNameCharacters: 'For this card, first and last name can only contain letters (A-Z) and spaces.',
  interCashNameLength: 'For this card, first and last name together can be at most 20 characters.',
  interCashPostalCode: 'For this card, a postal code is required.',
  // W14: what no application can be, whoever the processor is
  dateOfBirthInFuture: 'Date of birth must be a date in the past.',
  idExpired: 'This ID has expired. Use an ID that is still valid.',
  // Central Payments verifies identity on the SSN
  ssnRequired: 'Enter your 9-digit Social Security number, e.g. 123-45-6789.',
  dateOfBirthRequired: 'Date of birth is required.',
} as const;

/** OnGet: the name split once on the first space; the selects' first options. */
export function initialForm(displayName: string | null | undefined): OnboardingForm {
  const parts = (displayName ?? '').split(' ').filter((p) => p !== '');
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
    phoneNumber: '',
    dateOfBirth: '',
    placeOfBirth: '',
    gender: 'Female',
    nationality: '',
    idType: 'Passport',
    idNumber: '',
    idExpiryDate: '',
    preferredLanguage: 'en',
    addressCountry: '',
    addressRegion: '',
    addressCity: '',
    addressStreet: '',
    addressHouseNumber: '',
    addressPostalCode: '',
  };
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * OnPostAsync's checks, in order: the cardholder id of the session, then both dates. Then that the dates make sense
 * (W14), and for InterCash only (W13) the name and postal code as InterCash accepts them, so the cardholder is told
 * here and in plain words rather than by InterCash.
 */
export function checkOnboarding(
  cardholderId: string | null | undefined,
  form: OnboardingForm,
  provider?: OnboardingProvider,
  now: Date = new Date(),
): string | null {
  if (!cardholderId || !GUID.test(cardholderId)) return ONBOARDING_MESSAGES.noAccount;
  if (provider === 'opencp') {
    // no ID document here: the SSN is the check, and the date of birth
    if (!isDate(form.dateOfBirth)) return ONBOARDING_MESSAGES.dateOfBirthRequired;
    if (!(form.dateOfBirth < now.toISOString().slice(0, 10))) return ONBOARDING_MESSAGES.dateOfBirthInFuture;
    if (!/^\d{3}-?\d{2}-?\d{4}$/.test((form.ssn ?? '').trim())) return ONBOARDING_MESSAGES.ssnRequired;
    return null;
  }
  if (!isDate(form.dateOfBirth) || !isDate(form.idExpiryDate)) return ONBOARDING_MESSAGES.datesRequired;
  // W14: both are yyyy-MM-dd, compared with today's UTC date (an ID is good through its last day)
  const today = now.toISOString().slice(0, 10);
  if (!(form.dateOfBirth < today)) return ONBOARDING_MESSAGES.dateOfBirthInFuture;
  if (form.idExpiryDate < today) return ONBOARDING_MESSAGES.idExpired;
  if (provider === 'intercash') {
    const names = [form.firstName, form.lastName];
    if (names.some((name) => !/^[A-Za-z ]+$/.test(name))) return ONBOARDING_MESSAGES.interCashNameCharacters;
    if (names[0].length + names[1].length > 20) return ONBOARDING_MESSAGES.interCashNameLength;
    if ((form.addressPostalCode ?? '').trim() === '') return ONBOARDING_MESSAGES.interCashPostalCode;
  }
  return null;
}

const isDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));

/** The request body: an empty postal code is null; InterCash has no house-number field, so it leads the street line. */
export function onboardingRequest(provider: OnboardingProvider, form: OnboardingForm): OnboardingForm {
  const body = { ...form, addressPostalCode: form.addressPostalCode?.trim() ? form.addressPostalCode : null };
  // Central Payments takes no ID document: only the SSN goes, nothing typed in fields it does not use
  if (provider === 'opencp')
    return { ...body, idType: '', idNumber: '', idExpiryDate: '', placeOfBirth: '', ssn: form.ssn?.trim() ?? '' };
  delete body.ssn;
  if (provider === 'intercash' && form.addressHouseNumber.trim() !== '')
    body.addressStreet = `${form.addressHouseNumber} ${form.addressStreet}`;
  return body;
}

/** MDP's own read side can lag its customers/issue: look for the card a few times before going to the account. */
export const MDP_POLL = { attempts: 5, delayMs: 1500 } as const;
